import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  Slide,
  IconButton,
  Grid,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import ThemedDialogContent from "./ThemedDialogContent";
import ThemedDialogTitle from "./ThemedDialogTitle";
import ThemedDialogActions from "./ThemedDialogActions";
import ThemedTextField from "../ThemedTextField";
import ThemedAutocomplete from "../ThemedAutocomplete";
import { decoratedFetch } from "../../req_utils";
import {
  DARK_THEME_BODY_COLOR,
  DARK_THEME_TEXT_COLOR,
  LIGHT_THEME_TEXT_COLOR,
} from "../../constants";
import CloseIcon from "@mui/icons-material/Close";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function MobileEditAddressDialog({
  parentState,
  createDialogOpen,
  handleClose,
  handleCreateClose,
  onAddressUpdate,
  onCreateAddress,
  accountId,
  setSnackbarMessage,
}) {
  const darkThemeEnabled = useSelector((state) => state.darkThemeEnabled);
  const [address, setAddress] = useState(null);
  const [addressType, setAddressType] = useState("");
  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [addressTypes, setAddressTypes] = useState([]);
  const [createLoading, setCreateLoading] = useState(false);
  const [isAddToAccountChecked, setIsAddToAccountChecked] = useState(false);
  const [hasAccountId, setHasAccountId] = useState(false);

  // Check if all required fields are filled
  const isFormValid =
    addressType.trim() !== "" &&
    line1.trim() !== "" &&
    city.trim() !== "" &&
    state.trim() !== "" &&
    zip.trim() !== "";

  const initializeAddressData = (selectedAddress) => {
    if (selectedAddress) {
      setAddress(selectedAddress);
      setAddressType(selectedAddress.project_address_type || "");
      setLine1(selectedAddress.address?.line_1 || "");
      setLine2(selectedAddress.address?.line_2 || "");
      setCity(selectedAddress.address?.city || "");
      setState(selectedAddress.address?.state || "");
      setZip(selectedAddress.address?.zip || "");
      setIsAddToAccountChecked(Boolean(selectedAddress?.account_id));
      setHasAccountId(Boolean(selectedAddress?.account_id));
    }
  };

  const resetCreateForm = () => {
    setAddressType("");
    setLine1("");
    setLine2("");
    setCity("");
    setState("");
    setZip("");
    setCreateLoading(false);
    setIsAddToAccountChecked(false);
  };

  const listAddressTypes = () => {
    decoratedFetch("/list_project_address_types")
      .then((response) => response.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setAddressTypes(data);
        } else {
          console.error("Expected an array of address types, received:", data);
          setAddressTypes([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching address types:", error);
        setAddressTypes([]);
      });
  };

  useEffect(() => {
    if (parentState !== null && parentState?.selectedAddress !== null) {
      initializeAddressData(parentState?.selectedAddress);
    } else {
      setAddress(null);
    }
    if (parentState !== null || createDialogOpen) listAddressTypes();
  }, [parentState, createDialogOpen]);

  useEffect(() => {
    if (createDialogOpen) {
      resetCreateForm();
    }
  }, [createDialogOpen]);

  const updateAddress = useCallback(
    (fieldName, value) => {
      if (!address) return;

      decoratedFetch(`/update_address/${address.id}`, {
        method: "PUT",
        body: JSON.stringify({ [fieldName]: value }),
      })
        .then((response) => {
          if (response.status === 200) {
            setSnackbarMessage("Address saved");
            if (onAddressUpdate) {
              const updatedAddress = { ...address };
              updatedAddress[fieldName] = value;
              setAddress(updatedAddress);
              onAddressUpdate(updatedAddress);
            }
            return;
          }
          throw new Error("Error message generated");
        })
        .catch((error) => {
          console.error("Error saving address", error);
          setSnackbarMessage("Failed to save address");
        });
    },
    [address, onAddressUpdate, setSnackbarMessage],
  );

  const handleAddressTypeChange = (event, newValue) => {
    const newAddressType = newValue;
    setAddressType(newAddressType);
  };

  const handleAddressTypeBlur = () => {
    if (parentState !== null && addressType !== address?.project_address_type) {
      updateAddress("address_type", addressType);
    }
  };

  const handleLine1Change = (event) => {
    const newLine1 = event.target.value;
    setLine1(newLine1);
  };

  const handleLine1Blur = () => {
    if (parentState !== null && line1 !== address?.address?.line_1) {
      updateAddress("address", {
        ...address?.address,
        line_1: line1,
      });
    }
  };

  const handleLine2Change = (event) => {
    const newLine2 = event.target.value;
    setLine2(newLine2);
  };

  const handleLine2Blur = () => {
    if (parentState !== null && line2 !== address?.address?.line_2) {
      updateAddress("address", {
        ...address?.address,
        line_2: line2,
      });
    }
  };

  const handleCityChange = (event) => {
    const newCity = event.target.value;
    setCity(newCity);
  };

  const handleCityBlur = () => {
    if (parentState !== null && city !== address?.address?.city) {
      updateAddress("address", {
        ...address?.address,
        city: city,
      });
    }
  };

  const handleStateChange = (event) => {
    const newState = event.target.value;
    setState(newState);
  };

  const isValidZip = () => {
    return /^\d{5}$/.test(zip);
  };

  const isValidState = () => {
    return /^[A-Za-z]{2}$/.test(state);
  };

  const handleStateBlur = () => {
    if (!isValidState()) {
      setSnackbarMessage("Invalid state format");
      return;
    }
    if (parentState !== null && state !== address?.address?.state) {
      updateAddress("address", {
        ...address?.address,
        state: state,
      });
    }
  };

  const handleZipChange = (event) => {
    const newZip = event.target.value;
    setZip(newZip);
  };

  const handleZipBlur = () => {
    if (!isValidZip()) {
      setSnackbarMessage("Invalid ZIP code format");
      return;
    }
    if (parentState !== null && zip !== address?.address?.zip) {
      updateAddress("address", {
        ...address?.address,
        zip: zip,
      });
    }
  };

  const handleCreateAddress = () => {
    if (!isValidZip()) {
      setSnackbarMessage("Invalid ZIP code format");
      return;
    }
    if (!isValidState()) {
      setSnackbarMessage("Invalid state format");
      return;
    }
    setCreateLoading(true);
    const payload = {
      address_type: addressType,
      address: {
        line_1: line1,
        line_2: line2,
        city: city,
        state: state,
        zip: zip,
      },
      account_id: accountId,
    };

    decoratedFetch("/create_address", {
      method: "POST",
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (response.status === 201) {
          return response.json();
        }
        throw new Error("Failed to create address");
      })
      .then((data) => {
        setCreateLoading(false);
        setSnackbarMessage("Address created");
        if (onCreateAddress) {
          // Transform the API response to match the expected format
          const transformedData = {
            id: data.project_address_id,
            project_address_type: addressType,
            address: data.address,
            account_id: accountId,
          };
          onCreateAddress(transformedData);
        }
      })
      .catch((error) => {
        console.error("Error creating address:", error);
        setCreateLoading(false);
        setSnackbarMessage("Failed to create address");
      });
  };

  return (
    <>
      <Dialog
        fullScreen
        open={parentState !== null || createDialogOpen}
        onClose={parentState !== null ? handleClose : handleCreateClose}
        TransitionComponent={Transition}
        aria-labelledby="edit-address-dialog-title"
      >
        <ThemedDialogTitle
          id="edit-address-dialog-title"
          sx={{
            backgroundColor: darkThemeEnabled
              ? DARK_THEME_BODY_COLOR
              : "#f7f7f7",
            p: "8px 16px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              style={{
                color: darkThemeEnabled
                  ? DARK_THEME_TEXT_COLOR
                  : LIGHT_THEME_TEXT_COLOR,
                fontSize: "1rem",
                fontWeight: "bold",
                marginRight: "18px",
              }}
            >
              {parentState !== null
                ? `${address?.project_address_type || ""} Address`
                : "Create Address"}
            </div>
            <div>
              <IconButton
                edge="start"
                color="inherit"
                onClick={parentState !== null ? handleClose : handleCreateClose}
                aria-label="close"
                style={{
                  color: darkThemeEnabled
                    ? DARK_THEME_TEXT_COLOR
                    : LIGHT_THEME_TEXT_COLOR,
                }}
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>
        </ThemedDialogTitle>
        <ThemedDialogContent
          sx={{
            backgroundColor: darkThemeEnabled
              ? DARK_THEME_BODY_COLOR
              : "#f7f7f7",
            p: 0,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Grid container sx={{ py: "20px", px: "15px" }}>
            <Grid item xs={12}>
              <ThemedAutocomplete
                id="address-type"
                value={addressType}
                freeSolo
                options={addressTypes.map((option) => option.display_name)}
                renderInput={(params) => (
                  <ThemedTextField {...params} label="Type" size="small" />
                )}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") {
                    handleAddressTypeChange(event, newInputValue);
                  }
                }}
                onChange={(event, newValue) => {
                  if (typeof newValue === "string") {
                    handleAddressTypeChange(event, newValue);
                  }
                }}
                onBlur={handleAddressTypeBlur}
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: "20px" }}>
              <ThemedTextField
                label="Address Line 1"
                fullWidth
                variant="outlined"
                value={line1}
                onChange={handleLine1Change}
                onBlur={handleLine1Blur}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: "20px" }}>
              <ThemedTextField
                label="Address Line 2"
                fullWidth
                variant="outlined"
                value={line2}
                onChange={handleLine2Change}
                onBlur={handleLine2Blur}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: "20px" }}>
              <ThemedTextField
                label="City"
                fullWidth
                variant="outlined"
                value={city}
                onChange={handleCityChange}
                onBlur={handleCityBlur}
                size="small"
              />
            </Grid>
            <Grid item xs={4} sx={{ mt: "20px", pr: "5px" }}>
              <ThemedTextField
                label="State"
                fullWidth
                variant="outlined"
                value={state}
                onChange={handleStateChange}
                onBlur={handleStateBlur}
                inputProps={{ maxLength: 2 }}
                size="small"
              />
            </Grid>
            <Grid item xs={8} sx={{ mt: "20px", pl: "5px" }}>
              <ThemedTextField
                label="Zip Code"
                fullWidth
                variant="outlined"
                value={zip}
                onChange={handleZipChange}
                onBlur={handleZipBlur}
                inputProps={{ maxLength: 5 }}
                size="small"
              />
            </Grid>
          </Grid>
        </ThemedDialogContent>
        {parentState === null && (
          <ThemedDialogActions>
            <LoadingButton
              loading={createLoading}
              disabled={!isFormValid}
              sx={{
                backgroundColor: createLoading ? "#565656 !important" : "auto",
                "&.Mui-disabled": {
                  backgroundColor: darkThemeEnabled
                    ? "#666666 !important"
                    : "#e0e0e0 !important",
                  color: darkThemeEnabled
                    ? "#999999 !important"
                    : "#757575 !important",
                },
              }}
              variant="contained"
              onClick={handleCreateAddress}
            >
              Create
            </LoadingButton>
          </ThemedDialogActions>
        )}
      </Dialog>
    </>
  );
}
